import React, { useState } from 'react';

import { useStyles } from './style';
import { Box, Button, IconButton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { Add, Check } from '@material-ui/icons';
import clsx from 'clsx';
import SvgEditIcon from '../../../../assets/icons/EditIcon';
import SvgTrash from '../../../../assets/icons/Trash';
import { FreightRuleForm } from './freight-rule-form';
import { useDeleteFreightRuleMutation, useGetFreightRulesQuery } from '../../../../api/freight-rule';
import { FreightRule } from '../../../../api/freight-rule/types';
import { capitalizeFirstLetter } from '../../../../shared/helpers/capitalize-first-letter';
import { Dialog } from '../../../../shared/components/dialog';
import { formatCurrency } from '../../../../shared/helpers/format-currency';

interface Props {
  topUpOrders: boolean;
  onTopUpOrdersChange: (isChecked: boolean) => void;
}

export const FreightRules: React.FC<Props> = ({ topUpOrders, onTopUpOrdersChange }) => {
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [deletedId, setDeletedId] = useState<number | null>(null);
  const { data } = useGetFreightRulesQuery();
  const rules: FreightRule[] = data?.freight_rules || [];

  const [deleteFreightRule] = useDeleteFreightRuleMutation();

  const onRemoveRule = async () => {
    try {
      if (deletedId) {
        await deleteFreightRule(deletedId).unwrap();
      }
    } catch (error) {
      // Error handled by api slice
    } finally {
      setDeletedId(null); // always close
    }
  };

  return (
    <div>
      <div className={classes.description}>
        Define custom delivery fees and requirements. These rules override the
        default delivery settings.
      </div>
      <div className={classes.description}>
        Each customer can have one rule, applied on their designated delivery days.
      </div>

      <TableContainer className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>RULE NAME</TableCell>
              <TableCell className={classes.tableHeadCell}>DELIVERY TYPE</TableCell>
              <TableCell className={classes.tableHeadCell}>DELIVERY FEE</TableCell>
              <TableCell className={classes.tableHeadCell}>CUT-OFF TIME</TableCell>
              <TableCell className={classes.tableHeadCell}>DEFAULT MIN ORDER VALUE</TableCell>
              <TableCell className={classes.tableHeadCell}>FREE DELIVERY THRESHOLD</TableCell>
              <TableCell className={classes.tableHeadCell}>CUSTOM DAY SETTINGS</TableCell>
              <TableCell className={classes.tableHeadCell}>DEFAULT</TableCell>
              <TableCell className={classes.tableHeadCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.length > 0 ? (
              rules.map((rule, index) => (
                <TableRow key={index} className={classes.tableRow}>
                  <TableCell className={clsx(classes.tableCell, classes.ruleNameCell)}>{rule.name}</TableCell>
                  <TableCell className={clsx(classes.tableCell, classes.deliveryType)}>{capitalizeFirstLetter(rule.delivery_type)}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {(rule.default_delivery_fee === null || rule.default_delivery_fee === '')
                      ? '-'
                      : formatCurrency(rule.default_delivery_fee)
                    }
                  </TableCell>
                  <TableCell className={classes.tableCell}>{rule.default_order_cut_off_time}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {(rule.default_minimum_order_value === null || rule.default_minimum_order_value === '')
                      ? '-'
                      : formatCurrency(rule.default_minimum_order_value)}
                  </TableCell>
                  <TableCell className={clsx(classes.tableCell, classes.deliveryThreshold)}>
                    {(rule.free_delivery_threshold === null || rule.free_delivery_threshold === '')
                      ? '-'
                      : `${formatCurrency(rule.free_delivery_threshold)}`}
                  </TableCell>
                  <TableCell className={clsx(classes.tableCell)}>
                    {rule.custom_day_overrides
                      ? <Box className={classes.daySettings} display="flex" alignItems="center"><Check style={{ color: '#51D9B0' }} /> YES</Box>
                      : <Box className={classes.noSettings}>NO</Box>}
                  </TableCell>
                  <TableCell className={clsx(classes.tableCell)}>
                    {rule.is_default 
                      ? <Box className={classes.daySettings} display="flex" alignItems="center"><Check style={{ color: '#51D9B0' }} /> YES</Box>
                      : <Box className={classes.noSettings}>NO</Box>}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.iconButton}>
                      <IconButton onClick={() => {
                          setSelectedId(rule.id!);
                          setIsModalOpen(true);
                        }}>
                        <SvgEditIcon className={classes.editIcon} />
                      </IconButton>
                      <IconButton onClick={() => setDeletedId(rule.id!)}>
                        <SvgTrash className={classes.deleteIcon} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={10} className={classes.emptyMessage}>
                  There are no custom delivery rules
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        startIcon={<Add />}
        className={classes.addRuleLink}
        onClick={() => {
          setSelectedId(null);
          setIsModalOpen(true);
        }}
      >
        Add New Rule
      </Button>
      <div className={classes.topUpSwitch}>
        <div className={classes.topUpText}>TOP-UP ORDER</div>
        <Switch
          checked={topUpOrders}
          color='primary'
          classes={{
            thumb: classes.thumb,
            track: classes.track,
            checked: classes.checked,
          }}
          onChange={(e) => onTopUpOrdersChange(e.target.checked) }
        />
      </div>
      {isModalOpen && <FreightRuleForm isModalOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} recordId={selectedId} />}
      {!!deletedId && (
          <Dialog
            title={`Are you sure you want to delete this rule?`}
            onConfirm={onRemoveRule}
            onCancel={setDeletedId.bind(null, null)}
            customClass={classes.dialog}
          />
        )}
    </div>
  );
};
